// 1. Enhanced useCheckoutProductStore.js
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { getGifts } from "../../utils/ItemGetGifts";
import { translateProductName } from "../../utils/translateProductName";

const useCheckoutProductStore = create(
  persist(
    (set, get) => ({
      // Existing state
      items: [],
      toTalItems: 0,
      subtotal: 0,
      selectedCartItemKeys: [],

      // New state for order management
      currentOrder: null,
      orderHistory: [],

      // Existing methods
      isCartItemSelected: (cart_item_key) => {
        const state = get();
        return state.selectedCartItemKeys.includes(cart_item_key);
      },

      addGiftItems: (item) => {
        
        const giftImage = getGifts(item);

        // Gộp thông tin gift vào item dưới dạng thuộc tính mới (nếu muốn)
        return {
          ...item,
          gift: {
            srcGift: giftImage,
          },
        };
      },

      translateItemName: (item)=>{
        
        const newName = translateProductName(item.name)

        return {
          ...item,
          name:newName
        }

      },

      addCheckoutItem: (item) => {
        
        const { cart_item_key } = item;
        const state = get();

        if (state.isCartItemSelected(cart_item_key)) {
          return;
        }

        const itemWithGift = state.addGiftItems(item);
        const newItem = state.translateItemName(itemWithGift)

        const updatedKeys = [...state.selectedCartItemKeys, cart_item_key];
        const updatedItems = [...state.items, newItem];
        const updatedTotalItems = state.toTalItems + item.quantity;
        const updatedSubtotal = updatedItems.reduce(
          (sum, i) => sum + i.price * i.quantity,
          0
        );

        set({
          selectedCartItemKeys: updatedKeys,
          items: updatedItems,
          subtotal: updatedSubtotal,
          toTalItems: updatedTotalItems,
        });
      },

      removeCheckoutItem: (cart_item_key) => {
        const state = get();
        const itemToRemove = state.items.find(
          (i) => i.cart_item_key === cart_item_key
        );
        if (!itemToRemove) {
          return;
        }

        const updatedKeys = state.selectedCartItemKeys.filter(
          (key) => key !== cart_item_key
        );
        const updatedItems = state.items.filter(
          (i) => i.cart_item_key !== cart_item_key
        );
        const updatedTotalItems = state.toTalItems - itemToRemove.quantity;
        const updatedSubtotal = updatedItems.reduce(
          (sum, i) => sum + i.price * i.quantity,
          0
        );

        set({
          selectedCartItemKeys: updatedKeys,
          items: updatedItems,
          subtotal: updatedSubtotal,
          toTalItems: updatedTotalItems,
        });
      },

      toggleSelectCartItem: (item) => {
        const { cart_item_key } = item;
        const state = get();
        const isSelected = state.isCartItemSelected(cart_item_key);

        if (isSelected) {
          state.removeCheckoutItem(cart_item_key);
        } else {
          state.addCheckoutItem(item);
        }
      },

      // NEW METHODS for order management

      // Tạo order từ checkout items và form data
      createOrderFromCheckout: (formData, orderTotals = null) => {
        const state = get();
        const total = orderTotals?.total || state.subtotal;

        const orderData = {
          orderId: `ORDER_${Date.now()}`, // Temporary ID, sẽ được thay thế bởi server
          items: state.items.map((item) => ({
            id: item.id,
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            total: item.price * item.quantity,
            image: item.image,
            selected: item.selected,
            cart_item_key: item.cart_item_key,
            checkout_source: item.checkout_source,
          })),
          customerInfo: {
            name: `${formData.firstName} ${formData.lastName}`.toUpperCase(),
            email: formData.email,
            phone: formData.phone,
            shippingAddress: `${
              formData.shippingAddress || formData.address
            }, ${formData.shippingCity || formData.city}, ${
              formData.shippingState || formData.state
            }, ${formData.shippingZipCode || formData.zipCode}, ${
              formData.shippingCountry || formData.Country
            }`,
            billingAddress: `${formData.address}, ${formData.city}, ${formData.state}, ${formData.zipCode}, ${formData.Country}`,
          },
          paymentInfo: {
            paymentMethod: formData.paymentMethod,
            subtotal: state.subtotal,
            total: total,
            status: "pending",
            createdAt: new Date().toISOString(),
          },
          orderComments: formData.orderComments || "",
          checkoutItemKeys: [...state.selectedCartItemKeys], // Lưu keys để xóa khỏi cart sau
        };

        set({ currentOrder: orderData });
        return orderData;
      },

      // Update order với thông tin thanh toán từ server
      updateOrderWithPaymentInfo: (paymentResponse) => {
        const state = get();
        if (!state.currentOrder) return null;

        const updatedOrder = {
          ...state.currentOrder,
          orderId: paymentResponse.order_id || state.currentOrder.orderId,
          paymentInfo: {
            ...state.currentOrder.paymentInfo,
            orderId: paymentResponse.order_id || state.currentOrder.orderId,
            transactionNo: paymentResponse.transaction_no || "N/A",
            amount:
              paymentResponse.amount || state.currentOrder.paymentInfo.total,
            paymentDate:
              paymentResponse.payment_date || new Date().toISOString(),
            status: paymentResponse.status || "pending",
          },
        };

        set({ currentOrder: updatedOrder });
        return updatedOrder;
      },

      // Hoàn thành order (chuyển vào lịch sử và clear checkout)
      completeOrder: (finalPaymentInfo = null) => {
        const state = get();
        if (!state.currentOrder) return null;

        let completedOrder = { ...state.currentOrder };

        // Update với thông tin thanh toán cuối cùng nếu có
        if (finalPaymentInfo) {
          completedOrder = {
            ...completedOrder,
            paymentInfo: {
              ...completedOrder.paymentInfo,
              ...finalPaymentInfo,
              completedAt: new Date().toISOString(),
            },
          };
        }

        // Thêm vào lịch sử đơn hàng
        const updatedHistory = [completedOrder, ...state.orderHistory];

        set({
          currentOrder: null,
          orderHistory: updatedHistory,
        });

        return completedOrder;
      },

      // Clear checkout items và current order
      clearCheckoutItems: () => {
        set({
          selectedCartItemKeys: [],
          items: [],
          subtotal: 0,
          toTalItems: 0,
          currentOrder: null,
        });
      },

      // Clear only checkout items but keep current order
      clearCheckoutItemsOnly: () => {
        set({
          selectedCartItemKeys: [],
          items: [],
          subtotal: 0,
          toTalItems: 0,
        });
      },

      // Get current order
      getCurrentOrder: () => get().currentOrder,

      // Get order history
      getOrderHistory: () => get().orderHistory,

      // Get checkout item keys for removing from cart
      getCheckoutItemKeys: () => get().selectedCartItemKeys,

      // Reset entire store
      resetStore: () => {
        set({
          items: [],
          toTalItems: 0,
          subtotal: 0,
          selectedCartItemKeys: [],
          currentOrder: null,
          orderHistory: [],
        });
      },
    }),
    {
      name: "checkout-storage",
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);

export default useCheckoutProductStore;
